import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Mail, DollarSign } from 'lucide-react';
import { Alert, Button, Card, CardBody, CardHeader, CardTitle } from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import { api } from '../lib/api';

const INPUT_CLS =
  'border-ink-200 focus:border-brand-500 focus:ring-brand-500/30 w-full rounded-lg bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2';

/**
 * Forex — operação (Pagamento Market Data + avisos semi-manuais).
 * O challenge Forex é VITALÍCIO (jul/2026): não existe mais renovação nem prazo,
 * então esta tela deixou de ter fila de renovação.
 */
export function ForexRenovationsPage() {
  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">Operação</p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Forex — operação
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Pagamento Market Data ($8/mês) e avisos de vencimento da plataforma BlackArrow.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <MaintenanceCard />
        <ForexEmailCard />
      </div>

      <Alert tone="info" title="Como funciona">
        O challenge Forex é vitalício — não tem prazo nem renovação. O Pagamento Market Data ($8) e
        os avisos são lançados pelo e-mail do trader nos cards acima.
      </Alert>
    </div>
  );
}

function MaintenanceCard() {
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const generate = useMutation({
    mutationFn: () => api.generateForexMaintenance(email.trim()),
    onSuccess: () => {
      setFeedback('Pagamento Market Data lançado. O trader paga na aba Plataformas.');
      setError(null);
      setEmail('');
    },
    onError: (e: unknown) =>
      setError(e instanceof PnlApiError ? e.api.message : 'Falha ao lançar a cobrança.'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pagamento Market Data Forex ($8/mês)</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-ink-500 text-sm">
          Lança o Pagamento Market Data mensal para um trader Forex funded (1 por trader).
        </p>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@do-trader.com"
          className={INPUT_CLS}
        />
        {feedback && (
          <Alert tone="success" title="Lançada">
            {feedback}
          </Alert>
        )}
        {error && (
          <Alert tone="danger" title="Falhou">
            {error}
          </Alert>
        )}
        <Button
          leadingIcon={<DollarSign size={14} />}
          loading={generate.isPending}
          disabled={!email.trim()}
          onClick={() => {
            setFeedback(null);
            generate.mutate();
          }}
        >
          Gerar cobrança
        </Button>
      </CardBody>
    </Card>
  );
}

function ForexEmailCard() {
  const [email, setEmail] = useState('');
  const [variant, setVariant] = useState<'d7' | 'd3' | 'd0' | 'late'>('d7');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => api.sendForexEmail(email.trim(), variant),
    onSuccess: () => {
      setFeedback('E-mail enviado.');
      setError(null);
    },
    onError: (e: unknown) =>
      setError(e instanceof PnlApiError ? e.api.message : 'Falha ao enviar o e-mail.'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Avisos Forex (semi-manual)</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-ink-500 text-sm">
          Dispara, sob demanda, o aviso de vencimento da plataforma BlackArrow para um trader.
        </p>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@do-trader.com"
          className={INPUT_CLS}
        />
        <select
          value={variant}
          onChange={(e) => setVariant(e.target.value as 'd7' | 'd3' | 'd0' | 'late')}
          className={INPUT_CLS}
        >
          <option value="d7">Aviso — 7 dias</option>
          <option value="d3">Aviso — 3 dias</option>
          <option value="d0">Aviso — vence hoje</option>
          <option value="late">Aviso — último dia de tolerância</option>
        </select>
        {feedback && (
          <Alert tone="success" title="Enviado">
            {feedback}
          </Alert>
        )}
        {error && (
          <Alert tone="danger" title="Falhou">
            {error}
          </Alert>
        )}
        <Button
          variant="secondary"
          leadingIcon={<Mail size={14} />}
          loading={send.isPending}
          disabled={!email.trim()}
          onClick={() => {
            setFeedback(null);
            send.mutate();
          }}
        >
          Enviar aviso
        </Button>
      </CardBody>
    </Card>
  );
}
